import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { LuInfo, LuX } from 'react-icons/lu';
import {
  DELAY_NOTES,
  STATUS_DESCRIPTIONS,
  STATUS_FILTER_NOTE,
  StatusPill,
} from './logisticStatus';

// A click-popover rather than a hover tooltip: four rules plus the delay
// definitions are more than a tooltip can hold, and people need to keep it open
// while they read the grid behind it.
//
// Rendered through a portal with fixed coordinates, because both hosts clip it
// otherwise — the dashboard's "Shipment detail" card is `overflow-hidden`, and the
// trigger sits close enough to the right edge that an absolutely positioned panel
// runs off-screen. Same approach as TwDropdown and DateRangePicker.
const ORDER = ['shipped', 'delayed', 'booked_pending', 'pending_booking'];

const PANEL_WIDTH = 340;
const ESTIMATED_HEIGHT = 380;
const GAP = 6;
const EDGE = 8;

const StatusLegend = ({ align = 'start', label = '' }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    setPosition(null);
  }, []);

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const width = panelRef.current?.offsetWidth || PANEL_WIDTH;
    const height = panelRef.current?.offsetHeight || ESTIMATED_HEIGHT;

    // Anchor to the requested edge, then pull back inside the viewport.
    let left = align === 'end' ? rect.right - width : rect.left;
    left = Math.max(EDGE, Math.min(left, window.innerWidth - width - EDGE));

    // Flip above the trigger when there is no room below it.
    let top = rect.bottom + GAP;
    if (top + height > window.innerHeight - EDGE) {
      const above = rect.top - height - GAP;
      top = above >= EDGE ? above : Math.max(EDGE, window.innerHeight - height - EDGE);
    }

    setPosition({ top, left });
  }, [align]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = event => {
      if (event.key !== 'Escape') return;
      close();
      triggerRef.current?.focus();
    };
    // The panel lives in a portal, so "outside" has to exclude it explicitly.
    const onPointerDown = event => {
      if (panelRef.current?.contains(event.target)) return;
      if (triggerRef.current?.contains(event.target)) return;
      close();
    };
    const reposition = () => place();

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, close, place]);

  const panel = open && createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Shipment status reference"
      style={{
        position: 'fixed',
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        width: PANEL_WIDTH,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: `max(240px, calc(100dvh - ${EDGE * 2}px))`,
        zIndex: 100001,
        // Measured on the first layout pass; keep it invisible until then so it
        // never flashes at the wrong coordinates.
        visibility: position ? 'visible' : 'hidden',
      }}
      className="tw-overflow-y-auto tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-shadow-xl"
    >
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-px-3 tw-py-2 tw-border-0 tw-border-b tw-border-solid tw-border-slate-100 tw-bg-white tw-sticky tw-top-0">
        <p className="tw-text-xs tw-font-bold tw-text-slate-800 tw-m-0">Shipment status reference</p>
        <button
          type="button"
          onClick={() => { close(); triggerRef.current?.focus(); }}
          aria-label="Close status reference"
          className="tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded tw-border-0 tw-bg-transparent tw-text-slate-400 hover:tw-bg-slate-100 hover:tw-text-slate-700 tw-cursor-pointer tw-touch-manipulation focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-500"
        >
          <LuX size={12} />
        </button>
      </div>

      <dl className="tw-m-0 tw-px-3 tw-py-2 tw-space-y-2">
        {ORDER.map(status => (
          <div key={status} className="tw-flex tw-items-start tw-gap-2">
            <dt className="tw-flex-shrink-0 tw-mt-0.5"><StatusPill status={status} /></dt>
            <dd className="tw-m-0 tw-text-xs tw-text-slate-600 tw-leading-relaxed">{STATUS_DESCRIPTIONS[status]}</dd>
          </div>
        ))}
      </dl>

      <p className="tw-px-3 tw-pb-2 tw-m-0 tw-text-[11px] tw-text-slate-500 tw-leading-relaxed">{STATUS_FILTER_NOTE}</p>

      <div className="tw-px-3 tw-py-2 tw-border-0 tw-border-t tw-border-solid tw-border-slate-100 tw-bg-slate-50">
        <p className="tw-text-[11px] tw-font-bold tw-text-slate-600 tw-m-0 tw-mb-1.5">How delay is measured</p>
        <dl className="tw-m-0 tw-space-y-1.5">
          {DELAY_NOTES.map(([term, meaning]) => (
            <div key={term} className="tw-flex tw-items-start tw-gap-2">
              <dt className="tw-flex-shrink-0 tw-w-[68px] tw-text-[11px] tw-font-semibold tw-text-slate-700">{term}</dt>
              <dd className="tw-m-0 tw-text-[11px] tw-text-slate-500 tw-leading-relaxed">{meaning}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>,
    document.body,
  );

  return (
    <div className="tw-relative tw-inline-flex tw-items-center tw-flex-shrink-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="What the shipment statuses mean"
        className="tw-inline-flex tw-items-center tw-gap-1 tw-h-8 tw-px-1.5 tw-rounded tw-border-0 tw-bg-transparent tw-text-slate-400 hover:tw-text-primary-600 hover:tw-bg-slate-50 tw-cursor-pointer tw-touch-manipulation focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-500"
      >
        <LuInfo size={14} aria-hidden="true" />
        {label && <span className="tw-text-xs tw-font-medium">{label}</span>}
      </button>
      {panel}
    </div>
  );
};

StatusLegend.propTypes = {
  // Which edge the panel hangs from before it is clamped to the viewport; use
  // 'end' when the trigger sits near the right.
  align: PropTypes.oneOf(['start', 'end']),
  label: PropTypes.string,
};

export default StatusLegend;
